import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useHotel } from "../contexts/HotelContext";
import { getApiUrl } from "../services/api";
import { getMailDeliveryHealth } from "../services/mailDeliveryHealthApi";
import { MailDeliveryHealthResponse } from "../types/mail-delivery-health.types";
import { StatusBadge } from "../components/common/StatusBadge";
import { APP_VERSION } from "../version";
import { pluralizePl } from "../utils/text.utils";
import { formatDateTime } from "../utils/date-formatters";

// SHA-256 hash of "status2024" - pre-computed
const STATUS_PAGE_PASSWORD_HASH =
  "060882a77a57cfe1281cd2367036fc451a447a244beb0fca8e3af08b541a0a97";
const SESSION_KEY = "status-page-unlocked";

async function sha256hex(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

const StatusPageGate: React.FC<{ onUnlock: () => void }> = ({ onUnlock }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const hash = await sha256hex(password);
    if (hash === STATUS_PAGE_PASSWORD_HASH) {
      sessionStorage.setItem(SESSION_KEY, "1");
      onUnlock();
    } else {
      setError(true);
      setPassword("");
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="bg-white border rounded-lg p-8 w-full max-w-sm shadow-sm">
        <h1 className="text-xl font-semibold mb-4 text-gray-800">
          System Status
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          This page is restricted. Enter the password to continue.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError(false);
            }}
            placeholder="Password"
            autoFocus
            className={`w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-600 ${
              error ? "border-red-400" : "border-gray-300"
            }`}
          />
          {error && (
            <p className="text-red-500 text-xs">Incorrect password.</p>
          )}
          <button
            type="submit"
            className="w-full bg-primary-700 text-white px-4 py-2 rounded text-sm hover:bg-primary-800"
          >
            Unlock
          </button>
        </form>
      </div>
    </div>
  );
};

interface StatusInfo {
  timestamp: string;
  frontend: {
    url: string;
    userAgent: string;
    cookies: string[];
  };
  backend: {
    healthCheck: "loading" | "success" | "error";
    healthResponse?: any;
    apiBase: string;
  };
  authentication: {
    hasToken: boolean;
    tokenExpiry?: string;
  };
  hotel: {
    current?: any;
    domain: string;
    currentHost?: string;
  };
  network: {
    onlineStatus: boolean;
  };
}

type MailDeliveryHealthState =
  | { kind: "loading" }
  | { kind: "unauthorized" }
  | { kind: "error" }
  | { kind: "success"; data: MailDeliveryHealthResponse };

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  parts.push(`${secs}s`);

  return parts.join(" ");
}

export const StatusPage: React.FC = () => {
  const [unlocked, setUnlocked] = useState<boolean>(
    () => sessionStorage.getItem(SESSION_KEY) === "1"
  );

  const [status, setStatus] = useState<StatusInfo>({
    timestamp: new Date().toISOString(),
    frontend: {
      url: window.location.href,
      userAgent: navigator.userAgent,
      cookies: document.cookie.split("; ").filter((c) => c.length > 0),
    },
    backend: {
      healthCheck: "loading",
      apiBase: getApiUrl(),
    },
    authentication: {
      hasToken: !!localStorage.getItem("token"),
    },
    hotel: {
      domain: window.location.hostname,
    },
    network: {
      onlineStatus: navigator.onLine,
    },
  });

  const [mailDeliveryHealth, setMailDeliveryHealth] =
    useState<MailDeliveryHealthState>({ kind: "loading" });

  const { user, currentHost } = useHotel();

  const fetchMailDeliveryHealth = useCallback(async () => {
    setMailDeliveryHealth({ kind: "loading" });
    try {
      const data = await getMailDeliveryHealth();
      setMailDeliveryHealth({ kind: "success", data });
    } catch (error) {
      const authStatus = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;
      if (authStatus === 401 || authStatus === 403) {
        setMailDeliveryHealth({ kind: "unauthorized" });
      } else {
        setMailDeliveryHealth({ kind: "error" });
      }
    }
  }, []);

  useEffect(() => {
    fetchMailDeliveryHealth();
  }, [fetchMailDeliveryHealth]);

  useEffect(() => {
    // Update hotel info
    setStatus((prev) => ({
      ...prev,
      hotel: {
        ...prev.hotel,
        current: user,
        currentHost: currentHost,
      },
    }));
  }, [user, currentHost]);

  useEffect(() => {
    // Check authentication token
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        setStatus((prev) => ({
          ...prev,
          authentication: {
            hasToken: true,
            tokenExpiry: new Date(payload.exp * 1000).toISOString(),
          },
        }));
      } catch (e) {
        setStatus((prev) => ({
          ...prev,
          authentication: {
            hasToken: true,
            tokenExpiry: "Invalid token format",
          },
        }));
      }
    }
  }, []);

  const testBackendHealth = useCallback(async () => {
    setStatus((prev) => ({
      ...prev,
      backend: { ...prev.backend, healthCheck: "loading" },
    }));

    try {
      const response = await fetch(`${status.backend.apiBase}/health`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-Original-Host": window.location.hostname,
          ...(localStorage.getItem("token") && {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          }),
        },
      });

      const data = await response.json();

      setStatus((prev) => ({
        ...prev,
        backend: {
          ...prev.backend,
          healthCheck: response.ok ? "success" : "error",
          healthResponse: {
            status: response.status,
            statusText: response.statusText,
            data: data,
            headers: Object.fromEntries(response.headers.entries()),
          },
        },
      }));
    } catch (error) {
      setStatus((prev) => ({
        ...prev,
        backend: {
          ...prev.backend,
          healthCheck: "error",
          healthResponse: {
            error: error instanceof Error ? error.message : "Unknown error",
            type: "Network/CORS Error",
          },
        },
      }));
    }
  }, [status.backend.apiBase]);

  const testBackendAuth = async () => {
    try {
      const response = await fetch(`${status.backend.apiBase}/auth/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-Original-Host": window.location.hostname,
          ...(localStorage.getItem("token") && {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          }),
        },
      });

      const data = await response.json();

      alert(
        `Auth test response: ${response.status} - ${JSON.stringify(data, null, 2)}`
      );
    } catch (error) {
      alert(
        `Auth test error: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  };

  const testOffersAPI = async () => {
    try {
      const response = await fetch(`${status.backend.apiBase}/offers`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-Original-Host": window.location.hostname,
          ...(localStorage.getItem("token") && {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          }),
        },
      });

      const data = await response.json();

      alert(
        `Offers API test response: ${response.status} - ${JSON.stringify(data, null, 2)}`
      );
    } catch (error) {
      alert(
        `Offers API test error: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  };

  useEffect(() => {
    testBackendHealth();
  }, [testBackendHealth]);

  const refreshStatus = () => {
    setStatus((prev) => ({ ...prev, timestamp: new Date().toISOString() }));
    testBackendHealth();
    fetchMailDeliveryHealth();
  };

  if (!unlocked) {
    return <StatusPageGate onUnlock={() => setUnlocked(true)} />;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="status-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">System Status & Diagnostics</h1>
        <button
          onClick={refreshStatus}
          className="bg-primary-700 text-white px-4 py-2 rounded hover:bg-primary-800"
          data-testid="refresh-status-btn"
        >
          Refresh Status
        </button>
      </div>

      <div className="space-y-6">
        {/* Błędy wysyłki alertów o naborach (F2) — sekcja świadomie po polsku, patrz L-7 */}
        <div
          className="bg-white border rounded-lg p-4"
          data-testid="mail-delivery-health-section"
        >
          <h2 className="text-lg font-semibold mb-3 text-gray-900">
            Błędy wysyłki alertów o naborach
          </h2>
          {mailDeliveryHealth.kind === "loading" && (
            <div
              className="text-sm text-gray-500"
              data-testid="mail-delivery-health-loading"
            >
              Ładowanie…
            </div>
          )}
          {mailDeliveryHealth.kind === "unauthorized" && (
            <div
              className="text-sm text-gray-600"
              data-testid="mail-delivery-health-unauthorized"
            >
              Zaloguj się jako admin
            </div>
          )}
          {mailDeliveryHealth.kind === "error" && (
            <div
              className="text-sm text-red-600"
              data-testid="mail-delivery-health-error"
            >
              Nie udało się pobrać danych o błędach wysyłki.
            </div>
          )}
          {mailDeliveryHealth.kind === "success" && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <StatusBadge
                  status={
                    mailDeliveryHealth.data.failedCount > 0 ? "error" : "success"
                  }
                  label={
                    mailDeliveryHealth.data.failedCount > 0 ? "ALARM" : "OK"
                  }
                  aria-label={
                    mailDeliveryHealth.data.failedCount > 0
                      ? "Status wysyłki alertów: ALARM, wykryto błędy"
                      : "Status wysyłki alertów: OK, brak błędów"
                  }
                  data-testid="mail-delivery-health-badge"
                />
                <span data-testid="mail-delivery-health-summary">
                  {mailDeliveryHealth.data.failedCount}{" "}
                  {pluralizePl(
                    mailDeliveryHealth.data.failedCount,
                    "błąd",
                    "błędy",
                    "błędów"
                  )}
                  .{" "}
                  {mailDeliveryHealth.data.lastSuccessAt
                    ? `Ostatnia udana wysyłka: ${formatDateTime(
                        mailDeliveryHealth.data.lastSuccessAt
                      )}`
                    : "brak danych o udanej wysyłce"}
                </span>
              </div>
              <div
                className="text-xs text-gray-500"
                data-testid="mail-delivery-health-caption"
              >
                liczone po 3 nieudanych próbach
              </div>
              <div>
                <strong>Liczba dotkniętych tenantów:</strong>{" "}
                <span data-testid="mail-delivery-health-affected-tenants">
                  {mailDeliveryHealth.data.affectedTenants}
                </span>
              </div>
              {mailDeliveryHealth.data.failedCount > 0 &&
                mailDeliveryHealth.data.lastError && (
                  <div>
                    <strong>Ostatni błąd:</strong>
                    <div
                      className="font-mono bg-gray-100 p-2 mt-1 rounded text-xs"
                      data-testid="mail-delivery-health-last-error"
                    >
                      {mailDeliveryHealth.data.lastError}
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>

        {/* Version Info */}
        <div className="bg-white border rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-3 text-teal-700">
            Version Info
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <strong>Frontend Version:</strong>
              <div
                className="font-mono bg-gray-100 p-2 mt-1 rounded"
                data-testid="frontend-version"
              >
                {APP_VERSION}
              </div>
            </div>
            <div>
              <strong>Backend Version:</strong>
              <div
                className="font-mono bg-gray-100 p-2 mt-1 rounded"
                data-testid="backend-version"
              >
                {status.backend.healthResponse?.data?.version || "loading..."}
              </div>
            </div>
            <div>
              <strong>Deploy Branch:</strong>
              <div
                className="font-mono bg-gray-100 p-2 mt-1 rounded overflow-hidden text-ellipsis"
                data-testid="deploy-branch"
                title={status.backend.healthResponse?.data?.branch || ""}
              >
                {status.backend.healthCheck === "loading"
                  ? "loading..."
                  : status.backend.healthResponse?.data?.branch || "unknown"}
              </div>
            </div>
            <div>
              <strong>Deploy Commit:</strong>
              <div
                className="font-mono bg-gray-100 p-2 mt-1 rounded"
                data-testid="deploy-commit"
              >
                {status.backend.healthCheck === "loading"
                  ? "loading..."
                  : status.backend.healthResponse?.data?.commit || "unknown"}
              </div>
            </div>
          </div>
        </div>

        {/* Frontend Status */}
        <div className="bg-white border rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-3 text-green-700">
            Frontend Status
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <strong>Current URL:</strong>
              <div
                className="font-mono bg-gray-100 p-2 mt-1 rounded"
                data-testid="current-url"
              >
                {status.frontend.url}
              </div>
            </div>
            <div>
              <strong>Domain:</strong>
              <div
                className="font-mono bg-gray-100 p-2 mt-1 rounded"
                data-testid="current-domain"
              >
                {status.hotel.domain}
              </div>
            </div>
            <div>
              <strong>User Agent:</strong>
              <div className="font-mono bg-gray-100 p-2 mt-1 rounded text-xs">
                {status.frontend.userAgent}
              </div>
            </div>
            <div>
              <strong>Cookies ({status.frontend.cookies.length}):</strong>
              <div className="font-mono bg-gray-100 p-2 mt-1 rounded text-xs max-h-20 overflow-y-auto">
                {status.frontend.cookies.length > 0
                  ? status.frontend.cookies.join("\n")
                  : "No cookies"}
              </div>
            </div>
          </div>
        </div>

        {/* Backend Status */}
        <div className="bg-white border rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-3 text-blue-700">
            Backend Status
          </h2>
          <div className="space-y-3">
            <div>
              <strong>API Base URL:</strong>
              <div
                className="font-mono bg-gray-100 p-2 mt-1 rounded"
                data-testid="api-base-url"
              >
                {status.backend.apiBase}
              </div>
            </div>
            <div>
              <strong>Backend Uptime:</strong>
              <div
                className="font-mono bg-gray-100 p-2 mt-1 rounded"
                data-testid="backend-uptime"
              >
                {status.backend.healthResponse?.data?.uptime != null
                  ? formatUptime(status.backend.healthResponse.data.uptime)
                  : "loading..."}
              </div>
            </div>
            <div>
              <strong>Health Check:</strong>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={`px-2 py-1 rounded text-sm font-medium ${
                    status.backend.healthCheck === "loading"
                      ? "bg-yellow-100 text-yellow-800"
                      : status.backend.healthCheck === "success"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                  }`}
                  data-testid="health-check-status"
                >
                  {status.backend.healthCheck.toUpperCase()}
                </span>
                <button
                  onClick={testBackendHealth}
                  className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600"
                  data-testid="test-health-btn"
                >
                  Test Health
                </button>
                <button
                  onClick={testBackendAuth}
                  className="bg-purple-500 text-white px-3 py-1 rounded text-sm hover:bg-purple-600"
                  data-testid="test-auth-btn"
                >
                  Test Auth
                </button>
                <button
                  onClick={testOffersAPI}
                  className="bg-orange-500 text-white px-3 py-1 rounded text-sm hover:bg-orange-600"
                  data-testid="test-offers-btn"
                >
                  Test Offers API
                </button>
              </div>
            </div>
            {status.backend.healthResponse && (
              <div>
                <strong>Response:</strong>
                <pre
                  className="bg-gray-100 p-2 mt-1 rounded text-xs overflow-x-auto"
                  data-testid="health-response"
                >
                  {JSON.stringify(status.backend.healthResponse, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* Authentication Status */}
        <div className="bg-white border rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-3 text-purple-700">
            Authentication Status
          </h2>
          <div className="space-y-2 text-sm">
            <div>
              <strong>Has Token:</strong>
              <span
                className={`ml-2 px-2 py-1 rounded text-sm ${
                  status.authentication.hasToken
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
                data-testid="has-token-status"
              >
                {status.authentication.hasToken ? "YES" : "NO"}
              </span>
            </div>
            {status.authentication.tokenExpiry && (
              <div>
                <strong>Token Expiry:</strong>
                <span className="ml-2 font-mono" data-testid="token-expiry">
                  {status.authentication.tokenExpiry}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Hotel Context */}
        <div className="bg-white border rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-3 text-indigo-700">
            Hotel Context
          </h2>
          <div className="text-sm space-y-2">
            <div>
              <strong>Current Host:</strong>
              <div
                className="font-mono bg-gray-100 p-2 mt-1 rounded"
                data-testid="current-host"
              >
                {status.hotel.currentHost}
              </div>
            </div>
            <div>
              <strong>Current User:</strong>
              <pre
                className="bg-gray-100 p-2 mt-1 rounded text-xs overflow-x-auto"
                data-testid="hotel-context"
              >
                {JSON.stringify(status.hotel.current, null, 2)}
              </pre>
            </div>
          </div>
        </div>

        {/* Network Status */}
        <div className="bg-white border rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-3 text-gray-700">
            Network Status
          </h2>
          <div className="text-sm">
            <strong>Online:</strong>
            <span
              className={`ml-2 px-2 py-1 rounded text-sm ${
                status.network.onlineStatus
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
              data-testid="online-status"
            >
              {status.network.onlineStatus ? "YES" : "NO"}
            </span>
          </div>
        </div>

        {/* Timestamp */}
        <div
          className="text-xs text-gray-500 text-center"
          data-testid="status-timestamp"
        >
          Last updated: {status.timestamp}
        </div>
      </div>
    </div>
  );
};

export default StatusPage;
